import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendFileCreateRequest } from "~/classes/handlers/file/create"
import { sendFileDeleteRequests } from "~/classes/handlers/file/delete"
import { sendFolderListRequests } from "~/classes/handlers/file/list"
import { sendFileMoveRequests } from "~/classes/handlers/file/move"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"
import { sendFolderSubscribeRequest } from "~/classes/handlers/file/subscribe"
import { sendFileUpdateRequests } from "~/classes/handlers/file/update"

/**
 * useFileSystem is the file system handler for our system. This composable monitors
 * the state of the application, e.g. when the user navigates to other folders, and
 * sends the relevant cache requests to ensure that data in the application is
 * received. We also ensure that we aren't making too many calls, such as when editing
 * a field, or when navigating to a page and the path, waterfall, and folder view all
 * require the same data.
 *
 * Things we want to do:
 * - [ ] If the waterfall is open, grab files differently
 * - [ ] watch for scroll position, and then do more stuff (might require
 *         event handling)
 */
class FileSystem extends GlobalComposable {
  /**
   * List the contents of a folder, and return the requestId used to submit the request. The
   * default selection is everything and the default path selection is the most limited, to
   * make requests faster
   */
  listMultipleFoldersWithViewModeProperties = (
    folderIds: string[],
    pageSize: number,
    cacheOnly: boolean,
    selection?: EntityPropertySelection,
    pathSelection?: EntityPropertySelection,
  ) => {
    const { state: viewModeState, currentViewModeStateAsSelection, DEFAULT_SORT_DESCENDING, DEFAULT_SORT_PROPERTY } = useViewMode()

    if (!isDefined(selection))
      selection = currentViewModeStateAsSelection().inner()

    if (!isDefined(pathSelection))
      pathSelection = InterfaceSelection.minimumPathSelection().inner()

    // TODO: once we have property merging, only request properties needed in path, and merge
    //       them in
    // TODO: only request the properties required by the current view mode.

    const requests: FolderListRequestDatagram[] = []
    const requestIds: string[] = []

    let sort = DEFAULT_SORT_PROPERTY
    let desc = DEFAULT_SORT_DESCENDING

    for (const propSort of viewModeState.value.current.sort) {
      if (propSort.propertyId === PropertyId.FILE_VIRTUAL_SEARCH_RESULT) {
        console.error("Can't do a folder list request with search sorting, skipping")
        continue
      }
      sort = propSort.propertyId
      desc = propSort.descending

      if (len(viewModeState.value.current.sort) > 1)
        console.warn("Multi sorting not supported")
      break // FIXME
    }

    for (const folderId of folderIds) {
      const requestId = generateWorkerRequestId()
      const folder = StaticFile.blank(folderId)
      requests.push({
        requestId,
        folder: folder.inner(),
        selection,
        pathSelection,
        pageSize,
        sort,
        desc,
        offline: false,
      })
      requestIds.push(requestId)
    }
    sendFolderListRequests(requests)
    return requestIds
  }

  /**
   * This method adds a new value to a property (though under the hood its just calling
   * the update API. Note that we don't need to change the property within the
   * `StaticFile` itself as the update notification should be where the UI makes that
   * change to ensure state consistency across the app, and the cache should return
   * right away anyway.
   * @param file The file to update
   * @param property The new property
   */
  addFileProperty = (file: StaticFile, property: FileProperty) => {
    const { state: myself } = useSelf()

    if (!isDefined(file.id))
      throw new Error("Cannot update file without a file id, might be still loading")
    if (!isDefined(myself.value.user))
      throw new Error("Cannot update file without a user object")

    const timestamp = uuidv7()
    file = StaticFile.blank(file.id)
    file.setModifiedAt(timestamp, myself.value.user.id)
    file.addProperty({
      ...property,
      timestamp,
      status: PropertyStatus.StatusPending,
    })

    const requests = [{
      requestId: generateWorkerRequestId(),
      file: file.inner(),
      offline: false,
    }]
    sendFileUpdateRequests(requests)
    return requests
  }

  createPropertyWithDefaultValue = (file: StaticFile, propertyId: PropertyId) => {
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Not logged in")

    let value: EntityPropertyValue
    if (propertyId === PropertyId.FILE_NOTES)
      value = { type: PropertySchema.AnyStringValue, anyString: "" }
    else if (propertyId === PropertyId.FILE_RATING)
      value = { type: PropertySchema.PositiveFloatValue, positiveFloat: 5.0 }
    else if (propertyId === PropertyId.FILE_GENERATION_PROMPT)
      value = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "Add your prompt" }
    else if (propertyId === PropertyId.FILE_GENERATION_NEGATIVE_PROMPT)
      value = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "Add your negative prompt" }
    else if (propertyId === PropertyId.FILE_GENERATION_MODEL)
      value = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "Add your model" }
    else if (propertyId === PropertyId.FILE_GENERATION_MODEL_VERSION)
      value = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "Add your model version" }
    else if (propertyId === PropertyId.FILE_GENERATION_SOURCE)
      value = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "Add your source" }
    else if (propertyId === PropertyId.FILE_GENERATION_SEED)
      value = { type: PropertySchema.AnyIntegerValue, anyInteger: 0 }
    else
      return console.error("Property not supported", propertyId)

    const author = myself.value.user.id
    const timestamp = uuidv7()

    if (file.hasProperty(propertyId) || file.hasDeletedProperty(propertyId)) {
      return file.updateProperty(propertyId, value, false)
    }
    else {
      return this.addFileProperty(file, {
        id: file.id,
        propertyId,
        status: PropertyStatus.StatusPending,
        value,
        timestamp,
        author,
        version: 0,
        schema: value.type,
      })
    }
  }

  /**
   * TODO: refactor this since a move might result in a no-op
   * TODO: you need to update the navigation view so that if the root folder is not in the
   * parent of the destination, we remove the current object from the list of open folders
   */
  moveFiles = (files: Record<string, StaticFile>, destination: StaticFile) => {
    //
    const { notifyError } = useAlerts()
    const requests: FileMoveRequestDatagram[] = []

    for (const file of Object.values(files)) {
      // First we want to check if we can make the move:
      // - Is the file already in the destination
      // - Is the destination a cycle
      if (!destination.isFolder()) {
        // TODO: notify?
        console.error("Can't move file(s) into non-folder file")
        return
      }
      else if (file.id === destination.id) {
        if (len(files) > 1) {
          notifyError(
            "Can't move a file into its own subfolder",
            "Move the destination folder to a different location first",
          )
          console.error("Destination is one of selected files")
          return
        }
        else {
          console.error("Ignored drag as destination was dragged file")
          return
        }
      }
      else if (file.inferredParentId() === destination.id) {
        console.warn("File already in destination")
        return
      }
      for (const item of destination.path() ?? [])
        if (item.id === file.id) {
          notifyError(
            "Can't move a file into its own subfolder",
            "Move the destination folder to a different location first",
          )
          throw new Error("Destination is child of selected files")
        }
      // Now we want to send the update to the cache and let it fix our state on response
      const parentProp = clone(file.property(PropertyId.FILE_PARENT_ID))
      if (!(parentProp && "fileId" in parentProp.value))
        throw new Error("Can't move file as it has no parent property")

      parentProp.version = (parentProp.version ?? 0) + 1
      parentProp.value.fileId = destination.id
      parentProp.status = PropertyStatus.StatusPending

      const movedFile = StaticFile.blank(file.id)
      movedFile.addProperty(parentProp)

      requests.push({
        requestId: generateWorkerRequestId(),
        file: movedFile.inner(),
        offline: false,
        selection: InterfaceSelection.minimumInterfaceSelection().inner(),
      })
    }

    sendFileMoveRequests(requests)

    return requests
  }

  permanentlyDeleteFiles = (files: Record<string, StaticFile>) => {
    const requests: FileDeleteRequestDatagram[] = []

    for (const file of Object.values(files)) {
      if (file.isUserHomeFolder() || file.isUserArchiveFolder())
        throw new Error("Cannot delete system roots")
      // Now we want to send the update to the cache and let it fix our state on response
      const parentProp = clone(file.property(PropertyId.FILE_PARENT_ID))
      if (!parentProp)
        return console.error("Can't delete file as it has no parent property", file)

      parentProp.version = (parentProp.version ?? 0) + 1
      parentProp.status = PropertyStatus.StatusPending
      parentProp.deleted = true

      const deletedFile = StaticFile.blank(file.id)
      deletedFile.addProperty(parentProp)

      requests.push({
        requestId: generateWorkerRequestId(),
        file: deletedFile.inner(),
        offline: false,
      })
    }

    sendFileDeleteRequests(requests)
  }

  getFileProperties = (
    fileId: string,
    selection = InterfaceSelection.everything().inner(),
    pathSelection = InterfaceSelection.minimumPathSelection().inner(),
    requestId?: string,
  ) => {
    requestId = requestId || generateWorkerRequestId()
    sendFilePropertiesRequests([{
      requestId,
      fileId,
      pathSelection,
      selection,
      offline: false,
    }])
    return requestId
  }

  /**
   * Create a folder in the given parent folder, with the given name.
   * @param parent parent of the folder to create
   * @param name name of the folder to create
   * @param requestId (Optional) a request id to use for the request
   */
  createFolder = async (
    parent: StaticFile,
    name: string,
    requestId?: string,
  ) => {
    const { state: myself } = useSelf()
    const { storeCreatedFolder } = useCollectionView()

    if (!myself.value.user?.id)
      throw new Error("Cannot create a folder as an anon user yet")
    requestId = requestId || generateWorkerRequestId()
    const userId = myself.value.user!.id
    const preHash = StaticFile.clientPreHashId()
    const fileId = await StaticFile.idFromPrehash(preHash)
    const parentId = parent.id
    const file = StaticFile.newFolder({
      id: fileId,
      userId,
      name,
      parentId,
    })
    storeCreatedFolder(file.id)
    const request = {
      requestId,
      file: file.inner(),
      offline: false,
      preHash,
    }
    sendFileCreateRequest(request)
    return request
  }

  subscribeToFolderUpdates = (entityIds: string[]) => {
  // TODO: subscribe to entities not folders, change naming
    const requests: FolderSubscribeRequestDatagram[] = entityIds.map(folderId => ({ requestId: generateWorkerRequestId(), folderId, offline: false }))
    sendFolderSubscribeRequest(requests)
  }

  archiveFile = (file: StaticFile) => {
    const { state: myself } = useSelf()
    const { state: sharedDriveState } = useSharedDrives()
    if (file.getArchiveFolderFromPath())
      return console.error("File already archived, skipping")
    const root = file.getHomeFolderFromPath()
    if (!root)
      return console.error("Cannot archive file as it has no home folder", file)
    if (root.isUserHomeFolder()) {
      if (!myself.value.user?.id || !myself.value.archive)
        throw new Error("Cannot archive a file as an anon user yet")
      return this.moveFiles({ [file.id]: file }, myself.value.archive)
    }
    else if (root.isSharedDriveHome()) {
      const sharedDriveId = root.rootSharedDriveId()!
      const sharedDrive = sharedDriveState.value.all[sharedDriveId]
      if (!isDefined(sharedDrive))
        throw new Error("Cannot archive a file as a shared drive is not loaded yet")
      return this.moveFiles({ [file.id]: file }, sharedDrive.archive)
    }
    else {
      throw new Error("Cannot archive file, unknown root folder type")
    }
  }
}

const fileSystem = new FileSystem("file-system", theme.colors.orange.hex)
export default function () {
  return fileSystem.use()
}
